import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Tooltip,
  Skeleton,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Restaurant,
  OutdoorGrill,
  Home,
  People,
  EventSeat,
  CheckCircle,
  Block,
  Schedule,
  CleaningServices,
  RestartAlt,
  CallSplit,
  Person,
  Euro,
  ShoppingCart,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// Import Firebase table service
import { firebaseTableService } from '../../services/firebaseTableService';

interface RestaurantTable {
  id?: string;
  number: number;
  status: 'available' | 'occupied' | 'reserved' | 'cleaning';
  capacity: number;
  area: 'indoor' | 'outdoor';
  customer_name?: string;
  total_amount?: number;
  item_count?: number;
  start_time?: string;
}

// Split person data
interface SplitPerson {
  id: string;
  name: string;
  items: any[];
  total: number;
}

const TablesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTable, setEditingTable] = useState<RestaurantTable | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<RestaurantTable | null>(null);
  const [resetDialog, setResetDialog] = useState(false);
  const [tableToReset, setTableToReset] = useState<RestaurantTable | null>(null);
  const [resetAllDialog, setResetAllDialog] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0); // 0=all, 1=indoor, 2=outdoor

  // Split Table state
  const [splitDialog, setSplitDialog] = useState<{ open: boolean; table: RestaurantTable | null }>({ open: false, table: null });
  const [splitStep, setSplitStep] = useState(0); // 0=setup, 1=assign items, 2=summary
  const [splitPeople, setSplitPeople] = useState<SplitPerson[]>([]);
  const [splitLoading, setSplitLoading] = useState(false);
  const [tableOrders, setTableOrders] = useState<any[]>([]);
  const [tableItems, setTableItems] = useState<any[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [itemAssignments, setItemAssignments] = useState<Record<string, string>>({}); // itemKey -> personId
  const [personCount, setPersonCount] = useState(2);
  const [splitSaved, setSplitSaved] = useState(false);

  const [formData, setFormData] = useState({
    number: '',
    capacity: 4,
    area: 'indoor' as 'indoor' | 'outdoor',
  });

  useEffect(() => {
    // Real-time listener — auto-syncs with POS table state changes
    let unsub: (() => void) | undefined;
    const setupListener = async () => {
      try {
        setLoading(true);
        const { collection, onSnapshot } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        unsub = onSnapshot(collection(db, 'restaurant_tables'), (snap) => {
          const list: any[] = [];
          snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
          setTables(list as any);
          setLoading(false);
        }, (err) => {
          setError('Failed to load tables: ' + err.message);
          setLoading(false);
        });
      } catch (err: any) {
        setError('Failed to load tables: ' + err.message);
        setLoading(false);
      }
    };
    setupListener();
    return () => { if (unsub) unsub(); };
  }, []);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const firebaseTables = await firebaseTableService.getTables();
      setTables(firebaseTables);
    } catch (err: any) {
      setError('Failed to load tables: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const indoor = tables.filter(t => t.area === 'indoor');
    const outdoor = tables.filter(t => t.area === 'outdoor');
    const available = tables.filter(t => t.status === 'available');
    const occupied = tables.filter(t => t.status === 'occupied');
    const reserved = tables.filter(t => t.status === 'reserved');
    const cleaning = tables.filter(t => t.status === 'cleaning');
    const totalCapacity = tables.reduce((sum, t) => sum + t.capacity, 0);
    return { indoor, outdoor, available, occupied, reserved, cleaning, totalCapacity };
  }, [tables]);

  const filteredTables = useMemo(() => {
    let filtered = [...tables];
    if (activeTab === 1) filtered = filtered.filter(t => t.area === 'indoor');
    if (activeTab === 2) filtered = filtered.filter(t => t.area === 'outdoor');
    return filtered.sort((a, b) => {
      if (a.area !== b.area) return a.area === 'indoor' ? -1 : 1;
      return a.number - b.number;
    });
  }, [tables, activeTab]);

  // Get next available table number for an area
  const getNextAvailableNumber = (area: 'indoor' | 'outdoor'): number => {
    const areaTables = tables.filter(t => t.area === area);
    if (areaTables.length === 0) return 1;
    const numbers = areaTables.map(t => t.number).sort((a, b) => a - b);
    for (let i = 1; i <= numbers.length + 1; i++) {
      if (!numbers.includes(i)) return i;
    }
    return numbers.length + 1;
  };

  const handleOpenDialog = (table?: RestaurantTable) => {
    if (table) {
      setEditingTable(table);
      setFormData({
        number: table.number.toString(),
        capacity: table.capacity,
        area: table.area,
      });
    } else {
      const suggestedArea = formData.area || 'indoor';
      const nextNumber = getNextAvailableNumber(suggestedArea);
      setEditingTable(null);
      setFormData({
        number: nextNumber.toString(),
        capacity: 4,
        area: suggestedArea,
      });
    }
    setOpenDialog(true);
  };

  const handleAreaChange = (newArea: 'indoor' | 'outdoor') => {
    const nextNumber = getNextAvailableNumber(newArea);
    setFormData({
      ...formData,
      area: newArea,
      number: editingTable ? formData.number : nextNumber.toString(),
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTable(null);
    setFormData({ number: '', capacity: 4, area: 'indoor' });
  };

  const handleSave = async () => {
    try {
      const tableNumber = parseInt(formData.number);
      if (isNaN(tableNumber) || tableNumber <= 0) {
        setError('Table number must be a positive number');
        return;
      }

      const existingTableInArea = tables.find(
        t => t.area === formData.area &&
        t.number === tableNumber &&
        (!editingTable || t.id !== editingTable.id)
      );

      if (existingTableInArea) {
        setError(`Table number ${tableNumber} already exists in ${formData.area} area.`);
        return;
      }

      if (editingTable && editingTable.id) {
        await firebaseTableService.updateTable(editingTable.id, {
          number: tableNumber,
          capacity: formData.capacity,
          area: formData.area,
        });
        setSuccess('Table updated successfully!');
      } else {
        await firebaseTableService.createTable({
          number: tableNumber,
          capacity: formData.capacity,
          area: formData.area,
          status: 'available',
        });
        setSuccess('Table created successfully!');
      }

      handleCloseDialog();
      fetchTables();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError('Failed to save table: ' + err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeleteClick = (table: RestaurantTable) => {
    setTableToDelete(table);
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tableToDelete || !tableToDelete.id) return;

    try {
      await firebaseTableService.deleteTable(tableToDelete.id);
      setSuccess('Table deleted successfully!');
      setDeleteDialog(false);
      setTableToDelete(null);
      fetchTables();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError('Failed to delete table: ' + err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleResetClick = (table: RestaurantTable) => {
    setTableToReset(table);
    setResetDialog(true);
  };

  const handleResetConfirm = async () => {
    if (!tableToReset || !tableToReset.id) return;
    try {
      await firebaseTableService.resetTable(tableToReset.id);
      setSuccess(`Table ${tableToReset.number} (${tableToReset.area}) reset successfully!`);
      setResetDialog(false);
      setTableToReset(null);
      fetchTables();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError('Failed to reset table: ' + err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleResetAllConfirm = async () => {
    try {
      let resetCount = 0;
      for (const table of tables) {
        if (table.id && table.status !== 'available') {
          await firebaseTableService.resetTable(table.id);
          resetCount++;
        }
      }
      setSuccess(`${resetCount} tables reset successfully!`);
      setResetAllDialog(false);
      fetchTables();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError('Failed to reset tables: ' + err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  // ── SPLIT TABLE ─────────────────────────────────────────────────────────────

  const handleSplitClick = async (table: RestaurantTable) => {
    setSplitDialog({ open: true, table });
    setSplitStep(0);
    setSplitPeople([]);
    setItemAssignments({});
    setTableOrders([]);
    setTableItems([]);
    setTableTotal(0);
    setPersonCount(2);
    setSplitSaved(false);
    setSplitLoading(true);

    try {
      const { collection, query, where, getDocs, limit } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      const ordersRef = collection(db, 'orders');
      const tableNum = table.number;
      const tableNumStr = String(tableNum);

      let allItems: any[] = [];
      let total = 0;
      let orders: any[] = [];

      for (const tryNum of [tableNumStr, tableNum]) {
        const snap = await getDocs(query(
          ordersRef,
          where('table_number', '==', tryNum),
          where('order_type', '==', 'dine_in'),
          limit(20)
        ));
        if (!snap.empty) {
          snap.forEach(doc => {
            const d = doc.data();
            if (d.status !== 'cancelled' && d.status !== 'completed') {
              orders.push({ id: doc.id, ...d });
              total += parseFloat(d.total || 0);
              (d.items || []).forEach((item: any) => allItems.push({ ...item, _orderId: doc.id }));
            }
          });
          if (allItems.length > 0) break;
        }
      }

      // Fallback: use table's stored total if no orders found
      if (total === 0) total = table.total_amount || 0;

      setTableOrders(orders);
      setTableItems(allItems);
      setTableTotal(total);
    } catch (err) {
      console.error('[SPLIT ADMIN] Failed to load table orders:', err);
    } finally {
      setSplitLoading(false);
    }
  };

  // Build people list when person count changes
  const buildPeople = useCallback((count: number): SplitPerson[] => {
    return Array.from({ length: count }, (_, i) => ({
      id: `person_${i + 1}`,
      name: `Person ${i + 1}`,
      items: [],
      total: 0,
    }));
  }, []);

  const handleStartSplit = () => {
    const people = buildPeople(personCount);
    setSplitPeople(people);
    setSplitStep(1);
  };

  const handleAssignItem = (itemKey: string, personId: string) => {
    setItemAssignments(prev => ({ ...prev, [itemKey]: personId }));
  };

  // Calculate per-person totals from assignments
  const computedSplit = useMemo(() => {
    const assignedCount = Object.keys(itemAssignments).length;
    const unassigned = tableItems.length - assignedCount;

    const perPerson = splitPeople.map(person => {
      const myItems = tableItems.filter((_, idx) => {
        const key = `item_${idx}`;
        return itemAssignments[key] === person.id;
      });
      const total = myItems.reduce((sum, item) => {
        const price = parseFloat(item.price || item.unit_price || 0);
        const qty = parseInt(item.quantity || 1);
        return sum + price * qty;
      }, 0);
      return { ...person, items: myItems, total };
    });

    const assignedTotal = perPerson.reduce((s, p) => s + p.total, 0);
    const unassignedTotal = tableTotal - assignedTotal;

    return { perPerson, assignedCount, unassigned, unassignedTotal };
  }, [itemAssignments, splitPeople, tableItems, tableTotal]);

  // Equal split (no item assignment)
  const equalSplit = useMemo(() => {
    if (personCount < 1) return 0;
    return tableTotal / personCount;
  }, [tableTotal, personCount]);

  const handleSaveSplit = async () => {
    if (!splitDialog.table) return;
    setSplitLoading(true);
    try {
      const { collection, addDoc, serverTimestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');

      const splitData = {
        table_id: splitDialog.table.id || '',
        table_number: splitDialog.table.number,
        order_ids: tableOrders.map(o => o.id),
        total: tableTotal,
        person_count: personCount,
        people: computedSplit.perPerson.map(p => ({
          id: p.id,
          name: p.name,
          total: p.total > 0 ? p.total : equalSplit,
          item_count: p.items.length,
        })),
        item_assignments: itemAssignments,
        status: 'pending',
        created_by: 'Admin',
        created_at: serverTimestamp(),
      };

      await addDoc(collection(db, 'split_sessions'), splitData);
      setSplitSaved(true);
      setSplitStep(2);
    } catch (err) {
      console.error('[SPLIT ADMIN] Save failed:', err);
      setError('Failed to save split session.');
    } finally {
      setSplitLoading(false);
    }
  };

  const handleCloseSplit = () => {
    setSplitDialog({ open: false, table: null });
    setSplitStep(0);
    setSplitPeople([]);
    setItemAssignments({});
  };

  // ── STATUS HELPERS ───────────────────────────────────────────────────────────

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'available': return 'success';
      case 'occupied': return 'error';
      case 'reserved': return 'warning';
      case 'cleaning': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <CheckCircle sx={{ fontSize: 14 }} />;
      case 'occupied': return <Block sx={{ fontSize: 14 }} />;
      case 'reserved': return <Schedule sx={{ fontSize: 14 }} />;
      case 'cleaning': return <CleaningServices sx={{ fontSize: 14 }} />;
      default: return null;
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, flexWrap: 'wrap', gap: { xs: 1.5, md: 0 } }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('tables.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('tables.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<RestartAlt />}
              onClick={() => setResetAllDialog(true)}
              size="small"
              color="warning"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Reset All
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              size="small"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('tables.addTable')}
            </Button>
          </Box>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Compact Stats Row */}
        {loading ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {[...Array(7)].map((_, i) => (
              <Skeleton key={i} variant="rounded" width={120} height={32} />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              icon={<Restaurant sx={{ fontSize: 16 }} />}
              label={`${tables.length} ${t('common.total')}`}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 600 }}
            />
            <Chip
              icon={<Home sx={{ fontSize: 16 }} />}
              label={`${stats.indoor.length} ${t('tables.indoor')}`}
              color="primary"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<OutdoorGrill sx={{ fontSize: 16 }} />}
              label={`${stats.outdoor.length} ${t('tables.outdoor')}`}
              color="warning"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<People sx={{ fontSize: 16 }} />}
              label={`${stats.totalCapacity} ${t('tables.seats')}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: theme.palette.text.secondary }}
            />
            <Box sx={{ mx: 0.5, borderLeft: '1px solid', borderColor: 'divider' }} />
            <Chip
              icon={<CheckCircle sx={{ fontSize: 14 }} />}
              label={`${stats.available.length} ${t('common.available')}`}
              color="success"
              size="small"
            />
            {stats.occupied.length > 0 && (
              <Chip
                icon={<Block sx={{ fontSize: 14 }} />}
                label={`${stats.occupied.length} ${t('tables.occupied')}`}
                color="error"
                size="small"
              />
            )}
            {stats.reserved.length > 0 && (
              <Chip
                icon={<Schedule sx={{ fontSize: 14 }} />}
                label={`${stats.reserved.length} ${t('tables.reserved')}`}
                color="warning"
                size="small"
              />
            )}
            {stats.cleaning.length > 0 && (
              <Chip
                icon={<CleaningServices sx={{ fontSize: 14 }} />}
                label={`${stats.cleaning.length} ${t('tables.cleaning')}`}
                color="info"
                size="small"
              />
            )}
          </Box>
        )}

        {/* Area Tabs */}
        <Paper sx={{ mb: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
          >
            <Tab
              label={`${t('common.all')} (${tables.length})`}
              sx={{ textTransform: 'none', minHeight: 40, py: 0 }}
            />
            <Tab
              icon={<Home sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label={`${t('tables.indoor')} (${stats.indoor.length})`}
              sx={{ textTransform: 'none', minHeight: 40, py: 0 }}
            />
            <Tab
              icon={<OutdoorGrill sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label={`${t('tables.outdoor')} (${stats.outdoor.length})`}
              sx={{ textTransform: 'none', minHeight: 40, py: 0 }}
            />
          </Tabs>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, p: 1.5 }}>
            {loading ? (
              [...Array(4)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={90} sx={{ borderRadius: '10px' }} />
              ))
            ) : filteredTables.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <EventSeat sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {activeTab === 0
                    ? t('tables.noTables')
                    : t('tables.noTablesArea', { area: activeTab === 1 ? t('tables.indoor').toLowerCase() : t('tables.outdoor').toLowerCase() })}
                </Typography>
              </Box>
            ) : (
              filteredTables.map((table) => (
                <Box
                  key={table.id || `${table.area}-${table.number}`}
                  sx={{
                    backgroundColor: '#252838',
                    border: table.status === 'occupied' ? '1px solid rgba(244,67,54,0.4)' : '1px solid #3a3d52',
                    borderRadius: '10px',
                    p: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ bgcolor: '#00C896', color: '#1a1d2e', borderRadius: '8px', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1rem' }}>
                        {table.number}
                      </Box>
                      <Chip
                        label={table.area}
                        size="small"
                        color={table.area === 'indoor' ? 'primary' : 'warning'}
                        icon={table.area === 'indoor' ? <Home /> : <OutdoorGrill />}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <People sx={{ fontSize: 14, color: '#9ca3af' }} />
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600 }}>{table.capacity}</Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={table.status}
                      size="small"
                      color={getStatusColor(table.status)}
                      icon={getStatusIcon(table.status) || undefined}
                      sx={{ fontSize: '0.7rem', textTransform: 'capitalize', height: 22 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {table.customer_name && (
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                          {table.customer_name}
                        </Typography>
                      )}
                      {table.start_time && (
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                          since {new Date(table.start_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                      {!table.customer_name && !table.start_time && (
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>No customer</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      {table.status === 'occupied' && (
                        <IconButton size="small" onClick={() => handleSplitClick(table)} sx={{ color: '#6b46c1', width: 28, height: 28 }}>
                          <CallSplit sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => handleResetClick(table)} sx={{ color: '#ed6c02', width: 28, height: 28 }}>
                        <RestartAlt sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenDialog(table)} sx={{ color: '#1976d2', width: 28, height: 28 }}>
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(table)} sx={{ color: '#d32f2f', width: 28, height: 28 }}>
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {/* Desktop Table View */}
          <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 80 }}>{t('tables.tableNumber')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('tables.area')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90 }} align="center">{t('tables.capacity')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('common.status')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('tables.customer')}</TableCell>
                  {filteredTables.some(t => t.start_time) && (
                    <TableCell sx={{ fontWeight: 700 }}>{t('tables.since')}</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, width: 170 }} align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Skeleton width={30} /></TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Skeleton width={70} /></TableCell>
                      <TableCell align="center"><Skeleton width={30} sx={{ mx: 'auto' }} /></TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Skeleton width={80} /></TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Skeleton width={60} /></TableCell>
                      <TableCell align="right"><Skeleton width={80} sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))
                ) : filteredTables.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <EventSeat sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {activeTab === 0
                          ? t('tables.noTables')
                          : t('tables.noTablesArea', { area: activeTab === 1 ? t('tables.indoor').toLowerCase() : t('tables.outdoor').toLowerCase() })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTables.map((table) => (
                    <TableRow
                      key={table.id || `${table.area}-${table.number}`}
                      hover
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        ...(table.status === 'occupied' && {
                          bgcolor: 'rgba(244, 67, 54, 0.04)',
                        }),
                      }}
                    >
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                          {table.number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                        <Chip
                          label={table.area}
                          size="small"
                          color={table.area === 'indoor' ? 'primary' : 'warning'}
                          icon={table.area === 'indoor' ? <Home /> : <OutdoorGrill />}
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <People sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{table.capacity}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                        <Chip
                          label={table.status}
                          size="small"
                          color={getStatusColor(table.status)}
                          icon={getStatusIcon(table.status) || undefined}
                          sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                        <Typography variant="body2" color={table.customer_name ? 'text.primary' : 'text.disabled'}>
                          {table.customer_name || '—'}
                        </Typography>
                      </TableCell>
                      {filteredTables.some(t => t.start_time) && (
                        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
                          {table.start_time ? (
                            <Typography variant="body2" color="text.secondary">
                              {new Date(table.start_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          ) : '—'}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {/* Split Table — only for occupied tables */}
                        {table.status === 'occupied' && (
                          <Tooltip title="Split Table">
                            <IconButton
                              size="small"
                              onClick={() => handleSplitClick(table)}
                              sx={{ color: '#6b46c1' }}
                            >
                              <CallSplit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Reset Table">
                          <IconButton
                            size="small"
                            onClick={() => handleResetClick(table)}
                            color="warning"
                          >
                            <RestartAlt fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.edit')}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(table)}
                            color="primary"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(table)}
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* ── SPLIT TABLE DIALOG ─────────────────────────────────────────────── */}
        <Dialog
          open={splitDialog.open}
          onClose={handleCloseSplit}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{
            pb: 1,
            background: 'linear-gradient(135deg, #6b46c1 0%, #553c9a 100%)',
            color: '#1a1d2e',
            display: 'flex', alignItems: 'center', gap: 1
          }}>
            <CallSplit />
            Split Table {splitDialog.table?.number}
            <Chip
              label={`€${tableTotal.toFixed(2)} total`}
              size="small"
              sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.2)', color: '#1a1d2e', fontWeight: 700 }}
            />
          </DialogTitle>

          <DialogContent sx={{ pt: 2 }}>
            {splitLoading ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={36} sx={{ color: '#6b46c1' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Loading table orders...
                </Typography>
              </Box>
            ) : (

              /* ── STEP 0: Setup ─────────────────────────────────────── */
              splitStep === 0 ? (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Step 1 of 3 — How many people?
                  </Typography>

                  {/* Table summary */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 3 }, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShoppingCart sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          <b>{tableItems.length}</b> items
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Euro sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          Total: <b>€{tableTotal.toFixed(2)}</b>
                        </Typography>
                      </Box>
                      {splitDialog.table?.customer_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2">{splitDialog.table.customer_name}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>

                  {/* Person count */}
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Number of people</InputLabel>
                    <Select
                      value={personCount}
                      label="Number of people"
                      onChange={(e) => setPersonCount(Number(e.target.value))}
                    >
                      {[2, 3, 4, 5, 6, 7, 8].map(n => (
                        <MenuItem key={n} value={n}>{n} people</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Equal split preview */}
                  <Paper sx={{ p: 2, bgcolor: '#f3f0ff', borderRadius: 2, border: '1px solid #d6bcfa' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b46c1', mb: 1 }}>
                      Equal split preview
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {Array.from({ length: personCount }, (_, i) => (
                        <Chip
                          key={i}
                          icon={<Person sx={{ fontSize: 14 }} />}
                          label={`Person ${i + 1}: €${equalSplit.toFixed(2)}`}
                          size="small"
                          sx={{ bgcolor: '#1a1d2e', border: '1px solid #d6bcfa', fontWeight: 600 }}
                        />
                      ))}
                    </Box>
                  </Paper>

                  {tableItems.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                      Click "Next" to assign specific items to each person, or save as equal split.
                    </Typography>
                  )}
                </Box>
              )

              /* ── STEP 1: Assign Items ───────────────────────────────── */
              : splitStep === 1 ? (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Step 2 of 3 — Assign items to people
                  </Typography>

                  {/* Progress */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {computedSplit.assignedCount} / {tableItems.length} items assigned
                      </Typography>
                      {computedSplit.unassigned > 0 && (
                        <Typography variant="caption" color="warning.main">
                          {computedSplit.unassigned} unassigned
                        </Typography>
                      )}
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={tableItems.length > 0 ? (computedSplit.assignedCount / tableItems.length) * 100 : 0}
                      sx={{ borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#6b46c1' } }}
                    />
                  </Box>

                  {tableItems.length === 0 ? (
                    <Alert severity="info">No items found for this table. Use equal split instead.</Alert>
                  ) : (
                    <List dense disablePadding>
                      {tableItems.map((item, idx) => {
                        const itemKey = `item_${idx}`;
                        const assigned = itemAssignments[itemKey];
                        const price = parseFloat(item.price || item.unit_price || 0);
                        const qty = parseInt(item.quantity || 1);
                        return (
                          <ListItem
                            key={itemKey}
                            disableGutters
                            sx={{
                              py: 0.75,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              gap: 1,
                              bgcolor: assigned ? 'rgba(107,70,193,0.04)' : 'transparent',
                              borderRadius: 1,
                              px: 0.5,
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {qty}× {item.name || item.menu_item_name || 'Item'}
                                </Typography>
                              }
                              secondary={`€${(price * qty).toFixed(2)}`}
                              sx={{ flex: 1 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                              <Select
                                displayEmpty
                                value={assigned || ''}
                                onChange={(e) => handleAssignItem(itemKey, e.target.value)}
                                renderValue={(val) => val
                                  ? splitPeople.find(p => p.id === val)?.name || val
                                  : <span style={{ color: '#aaa' }}>Assign to…</span>
                                }
                                sx={{ fontSize: '0.8rem' }}
                              >
                                <MenuItem value=""><em>Unassigned</em></MenuItem>
                                {splitPeople.map(person => (
                                  <MenuItem key={person.id} value={person.id}>
                                    {person.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}

                  {/* Per-person running totals */}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Per-person totals</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {computedSplit.perPerson.map(p => (
                      <Chip
                        key={p.id}
                        icon={<Person sx={{ fontSize: 14 }} />}
                        label={`${p.name}: €${(p.total > 0 ? p.total : equalSplit).toFixed(2)}`}
                        size="small"
                        sx={{
                          bgcolor: p.total > 0 ? '#f3f0ff' : '#1e2139',
                          border: `1px solid ${p.total > 0 ? '#d6bcfa' : '#e0e0e0'}`,
                          fontWeight: 600,
                          color: p.total > 0 ? '#6b46c1' : 'text.secondary',
                        }}
                      />
                    ))}
                    {computedSplit.unassignedTotal > 0.01 && (
                      <Chip
                        label={`Unassigned: €${computedSplit.unassignedTotal.toFixed(2)}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              )

              /* ── STEP 2: Summary / Saved ────────────────────────────── */
              : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CheckCircle sx={{ fontSize: 56, color: '#38a169', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Split Plan Saved
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    The split session for Table {splitDialog.table?.number} has been saved to Firestore.
                    POS staff can now see this split and process payments per person.
                  </Typography>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                      Split Summary — {personCount} people
                    </Typography>
                    {computedSplit.perPerson.map(p => (
                      <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography variant="body2">{p.name}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#6b46c1' }}>
                          €{(p.total > 0 ? p.total : equalSplit).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>€{tableTotal.toFixed(2)}</Typography>
                    </Box>
                  </Paper>

                  <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                    Payments are processed at the POS terminal by staff.
                  </Alert>
                </Box>
              )
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
            {splitStep === 0 && !splitLoading && (
              <>
                <Button onClick={handleCloseSplit} size="small">Cancel</Button>
                <Button
                  onClick={handleSaveSplit}
                  variant="outlined"
                  size="small"
                  sx={{ color: '#6b46c1', borderColor: '#6b46c1' }}
                  disabled={splitLoading}
                >
                  Save Equal Split
                </Button>
                {tableItems.length > 0 && (
                  <Button
                    onClick={handleStartSplit}
                    variant="contained"
                    size="small"
                    sx={{ bgcolor: '#6b46c1', '&:hover': { bgcolor: '#553c9a' } }}
                  >
                    Assign Items →
                  </Button>
                )}
              </>
            )}
            {splitStep === 1 && (
              <>
                <Button onClick={() => setSplitStep(0)} size="small">Back</Button>
                <Button
                  onClick={handleSaveSplit}
                  variant="contained"
                  size="small"
                  sx={{ bgcolor: '#6b46c1', '&:hover': { bgcolor: '#553c9a' } }}
                  disabled={splitLoading}
                >
                  {splitLoading ? <CircularProgress size={16} /> : 'Save Split Plan'}
                </Button>
              </>
            )}
            {splitStep === 2 && (
              <Button onClick={handleCloseSplit} variant="contained" size="small" sx={{ bgcolor: '#6b46c1', '&:hover': { bgcolor: '#553c9a' } }}>
                Done
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* ── ADD / EDIT DIALOG ─────────────────────────────────────────────── */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            {editingTable ? t('tables.editTable') : t('tables.addNewTable')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>{t('tables.area')}</InputLabel>
                <Select
                  value={formData.area}
                  onChange={(e) => handleAreaChange(e.target.value as 'indoor' | 'outdoor')}
                  label={t('tables.area')}
                >
                  <MenuItem value="indoor">{t('tables.indoor')}</MenuItem>
                  <MenuItem value="outdoor">{t('tables.outdoor')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t('tables.tableNumber')}
                type="number"
                size="small"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t('tables.seatingCapacity')}
                type="number"
                size="small"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 4 })}
                margin="normal"
                required
                inputProps={{ min: 1, max: 20 }}
              />
              {!editingTable && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('tables.numberingSuggestion', { area: formData.area })}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} size="small">{t('common.cancel')}</Button>
            <Button onClick={handleSave} variant="contained" size="small">
              {editingTable ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs">
          <DialogTitle>{t('tables.deleteTable')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('tables.deleteTableConfirm', { number: tableToDelete?.number, area: tableToDelete?.area })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} size="small">{t('common.cancel')}</Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color="error" size="small">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Single Table Dialog */}
        <Dialog open={resetDialog} onClose={() => setResetDialog(false)} maxWidth="xs">
          <DialogTitle>Reset Table</DialogTitle>
          <DialogContent>
            <Typography>
              Reset Table {tableToReset?.number} ({tableToReset?.area}) to available? This will clear all customer data, order info, and tab data.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialog(false)} size="small">{t('common.cancel')}</Button>
            <Button onClick={handleResetConfirm} variant="contained" color="warning" size="small">
              Reset
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset All Tables Dialog */}
        <Dialog open={resetAllDialog} onClose={() => setResetAllDialog(false)} maxWidth="xs">
          <DialogTitle>Reset All Tables</DialogTitle>
          <DialogContent>
            <Typography>
              Reset ALL tables to available? This will clear all customer data, order info, and tab data from every table.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetAllDialog(false)} size="small">{t('common.cancel')}</Button>
            <Button onClick={handleResetAllConfirm} variant="contained" color="warning" size="small">
              Reset All
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default TablesPage;
